/**
 * Primary Group Screen — M_PrimaryGroup CRUD
 * Left: Form | Right: Chart of Accounts Tree
 * Fields: Primary Group Code, Primary Group Name*, Account Type* (dropdown), Schedule* (dropdown), Place of Head, Category (BS/PL radio)
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Save, Grid3X3, XCircle, Plus, RefreshCw, ArrowLeft } from 'lucide-react';
import ERPAdvancedGrid from '../../components/erp/ERPAdvancedGrid';
import ChartOfAccountsTree from '../../components/erp/ChartOfAccountsTree';
import DynamicFieldRenderer from '../../components/erp/DynamicFieldRenderer';
import { erpToast } from '../../components/erp/ERPToast';
import { FormHelp } from '../../components/help-engine/FormHelp';
import { HelpInfo } from '../../components/help-engine/HelpInfo';
import '../../styles/erp-theme.css';
import { API_BASE as API_URL, getToken} from '../../lib/api';

const token = () => getToken() || '';
const hdrs = () => ({ Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' });

const EMPTY = { PGroupCode: '', PGroupName: '', AccountType: 'Assets', Schedule: '', PlaceOfHead: '0', Category: 'BS' };

const PrimaryGroupScreen = ({ theme = 'light', navigateTo = () => {} }) => {
  const [view, setView] = useState('grid');
  const [allItems, setAllItems] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [mode, setMode] = useState('add');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [treeKey, setTreeKey] = useState(0);
  const token = getToken() || '';
  const userRole = (() => { try { return JSON.parse(localStorage.getItem('retailcliq_user') || '{}').role || ''; } catch { return ''; } })();
  const [dynValues, setDynValues] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/finance/chart-of-accounts`, { headers: hdrs() });
      const j = await r.json();
      if (j.success) {
        setAllItems(j.data?.primaryGroups || []);
        setSchedules(j.data?.schedules || []);
      }
    } catch { erpToast.error('Failed to load'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const COLUMNS = useMemo(() => [
    { field: 'PGroupId', header: 'Code' },
    { field: 'PGroupName', header: 'Primary Group Name' },
    { field: 'AccountType', header: 'Account Type' },
    { field: 'Schedule', header: 'Schedule' },
    { field: 'Category', header: 'Category' },
    { field: 'Status', header: 'Status' },
  ], []);

  const { data, filteredTotal } = useMemo(() => {
    let items = [...allItems];
    Object.entries(filters).forEach(([key, val]) => {
      if (val) { const s = val.toLowerCase(); items = items.filter(i => String(i[key] || '').toLowerCase().includes(s)); }
    });
    const filteredTotal = items.length;
    if (sortField) {
      items.sort((a, b) => {
        const av = String(a[sortField] || ''), bv = String(b[sortField] || '');
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      });
    }
    return { data: items.slice((page - 1) * pageSize, page * pageSize), filteredTotal };
  }, [allItems, filters, sortField, sortDir, page, pageSize]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const isView = mode === 'view';

  const openForm = (item, m) => {
    setForm(item ? { PGroupCode: item.PGroupId, PGroupName: item.PGroupName, AccountType: item.AccountType || item.PGroupType || 'Assets', Schedule: item.Schedule || item.Scheduleno || '', PlaceOfHead: item.PlaceOfHead || '0', Category: item.Category || 'BS' }
      : { ...EMPTY });
    setMode(m || 'add');
    setView('form');
    setError('');
  };

  const handleTreeSelect = (node) => {
    if (node.type === 'primary') {
      const item = allItems.find(d => d.PGroupId === node.code);
      if (item) openForm(item, 'edit');
    }
  };

  const handleSave = async () => {
    if (!form.PGroupName?.trim()) { setError('Primary Group Name is required'); return; }
    setSaving(true); setError('');
    try {
      const isEdit = mode === 'edit' && form.PGroupCode;
      const ptype = form.AccountType === 'Assets' ? '01' : form.AccountType === 'Liabilities' ? '02' : form.AccountType === 'Incomes' ? '03' : '04';
      const url = isEdit ? `${API_URL}/api/finance/primary-groups/${form.PGroupCode}` : `${API_URL}/api/finance/primary-groups`;
      const method = isEdit ? 'PUT' : 'POST';
      const payload = { PGroupName: form.PGroupName, Category: form.Category, PGroupType: ptype, Schedule: form.Schedule, Slno: form.PlaceOfHead || 0 };
      const r = await fetch(url, { method, headers: hdrs(), body: JSON.stringify(payload) });
      const j = await r.json();
      if (j.success) {
        erpToast.success(j.message || (mode === 'edit' ? 'Primary Group updated' : 'Primary Group saved'));
        if (j.PGroupId) setForm(p => ({ ...p, PGroupCode: j.PGroupId }));
        if (mode === 'edit') setMode('add');
        setTreeKey(k => k + 1);
        loadData();
      } else { setError(j.error || 'Save failed'); }
    } catch (e) { setError('Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Void this primary group?')) return;
    try {
      const r = await fetch(`${API_URL}/api/finance/primary-groups/${id}`, { method: 'DELETE', headers: hdrs() });
      const j = await r.json();
      if (j.success) { erpToast.success('Primary Group voided'); loadData(); setTreeKey(k => k + 1); }
      else erpToast.error(j.error || 'Void failed');
    } catch { erpToast.error('Failed to void'); }
  };

  const accountTypes = [
    { value: 'Assets', label: 'Assets' }, { value: 'Liabilities', label: 'Liabilities' },
    { value: 'Incomes', label: 'Incomes' }, { value: 'Expenses', label: 'Expenses' }
  ];

  if (view === 'grid') {
    return (
      <div className="erp-module" style={{ flexDirection: 'row' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <ERPAdvancedGrid
            title="Primary Group"
            columns={COLUMNS}
            data={data}
            total={filteredTotal}
            idField="PGroupId"
            nameField="PGroupName"
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            sortField={sortField}
            sortDir={sortDir}
            onSort={handleSort}
            filters={filters}
            onFilterChange={(f) => { setFilters(f); setPage(1); }}
            loading={loading}
            onAddNew={() => openForm(null, 'add')}
            onEdit={(row, m) => openForm(row, m)}
            onVoid={(row) => handleDelete(row.PGroupId)}
            testId="primary-group"
            onBack={() => navigateTo('HUB')}
            exportFileName="PrimaryGroup"
            cellRenderer={(field, value, row) => {
              if (field === 'AccountType') return value || row.PGroupType || '';
              if (field === 'Schedule') return value || row.Scheduleno || '';
              if (field === 'Category') return value === 'BS' ? 'Balance Sheet' : 'Profit & Loss';
              if (field === 'Status') return <span className={`erp-status-badge ${value === 'S' ? 'active' : 'inactive'}`}>{value === 'S' ? 'Active' : 'Inactive'}</span>;
              return null;
            }}
          />
        </div>
        <div style={{ width: 280, flexShrink: 0 }}><ChartOfAccountsTree onSelect={handleTreeSelect} refreshKey={treeKey} /></div>
      </div>
    );
  }

  // ─── FORM ───
  return (
    <FormHelp formName="PrimaryGroup" userRole={userRole}>
    <div className="erp-module" style={{ flexDirection: 'row' }} data-dev-component="PrimaryGroup" data-dev-file="screens/finance/PrimaryGroupScreen.js">
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div className="erp-screen-header">
          <button onClick={() => setView('grid')} data-testid="screen-back-btn" style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",cursor:"pointer",padding:"2px 0"}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 5-7 7 7 7"/></svg><span className="erp-screen-title" style={{color:"var(--erp-accent,#1856a7)"}}>Primary Group</span></button>
          <div style={{ fontSize: 12, fontWeight: 700, fontFamily: 'var(--erp-font-mono)', color: 'var(--erp-primary)' }}>{form.PGroupCode || 'New'}</div>
          <div className="erp-screen-actions">
            {!isView && <button data-testid="pg-save-btn" className="erp-btn erp-btn-ghost" onClick={handleSave} disabled={saving}>{mode === 'edit' ? <RefreshCw size={13} /> : <Save size={13} />} {saving ? 'Saving...' : mode === 'edit' ? 'Update' : 'Save'}</button>}
            <button className="erp-btn erp-btn-ghost" onClick={() => { setView('grid'); loadData(); }}><Grid3X3 size={13} /> Gridview</button>
            {!isView && <button className="erp-btn erp-btn-ghost" onClick={() => openForm(null, 'add')}><XCircle size={13} /> Clear</button>}
          </div>
        </div>
        {error && <div className="erp-alert-error">{error}</div>}
        <form style={{ padding: 'var(--erp-sp-4)', flex: 1, overflow: 'auto' }} onSubmit={e => e.preventDefault()}>
          <div className="erp-form-card"><div className="erp-form-section">
            <div style={{ maxWidth: 420 }}>
              <div className="erp-form-row"><label className="erp-form-label">Primary Group Code<HelpInfo controlId="PGroupCode" controlName="Primary Group Code" /></label><div className="erp-form-control"><input data-testid="pg-code" data-help-control="PGroupCode" value={form.PGroupCode} disabled className="erp-input" style={{ fontFamily: 'var(--erp-font-mono)' }} /></div></div>
              <div className="erp-form-row"><label className="erp-form-label required">Primary Group Name<HelpInfo controlId="PGroupName" controlName="Primary Group Name" /></label><div className="erp-form-control"><input data-testid="pg-name" data-help-control="PGroupName" value={form.PGroupName} onChange={e => set('PGroupName', e.target.value)} disabled={isView} className="erp-input" /></div></div>
              <div className="erp-form-row"><label className="erp-form-label required">Account Type<HelpInfo controlId="AccountType" controlName="Account Type" /></label><div className="erp-form-control">
                <select data-testid="pg-accttype" data-help-control="AccountType" value={form.AccountType} onChange={e => set('AccountType', e.target.value)} disabled={isView} className="erp-input erp-select">
                  {accountTypes.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select></div></div>
              <div className="erp-form-row"><label className="erp-form-label required">Schedule<HelpInfo controlId="Schedule" controlName="Schedule" /></label><div className="erp-form-control">
                <select data-testid="pg-schedule" data-help-control="Schedule" value={form.Schedule} onChange={e => set('Schedule', e.target.value)} disabled={isView} className="erp-input erp-select">
                  <option value="">--Select--</option>
                  {schedules.map(s => <option key={s.Scheduleno} value={s.Scheduleno}>{s.Scheduleno} — {s.ScheduleName}</option>)}
                </select></div></div>
              <div className="erp-form-row"><label className="erp-form-label">Place of Head<HelpInfo controlId="PlaceOfHead" controlName="Place of Head" /></label><div className="erp-form-control"><input data-testid="pg-poh" data-help-control="PlaceOfHead" value={form.PlaceOfHead} onChange={e => set('PlaceOfHead', e.target.value)} disabled={isView} className="erp-input" type="number" /></div></div>
              <div className="erp-form-row"><label className="erp-form-label">Category<HelpInfo controlId="Category" controlName="Category" /></label><div className="erp-form-control" style={{ display: 'flex', gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}><input type="radio" name="pg-cat" data-help-control="Category" value="BS" checked={form.Category === 'BS'} onChange={() => set('Category', 'BS')} disabled={isView} /> Balance Sheet</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}><input type="radio" name="pg-cat" data-help-control="Category" value="PL" checked={form.Category === 'PL'} onChange={() => set('Category', 'PL')} disabled={isView} /> Profit & Loss</label>
              </div></div>
            </div>
            <DynamicFieldRenderer screenName="PrimaryGroup" recordId={form.PGroupCode} token={token} values={dynValues} onChange={(k, v) => setDynValues(p => ({ ...p, [k]: v }))} readOnly={isView} userRole={userRole} />
          </div></div>
        </form>
      </div>
      <div style={{ width: 280, flexShrink: 0 }}><ChartOfAccountsTree onSelect={handleTreeSelect} selectedId={form.PGroupCode} refreshKey={treeKey} /></div>
    </div>
    </FormHelp>
  );
};

export default PrimaryGroupScreen;
